import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getTeamMembers, getOptimizedImageUrl, getFounders } from '../sanity/client';
import { useCachedTexts } from '../hooks/useCachedTexts';
import './TeamPage.css';

const TeamPage = () => {
  const { t, i18n } = useTranslation();
  const [founders, setFounders] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { getText } = useCachedTexts('team_founders');

  useEffect(() => {
    document.title = 'Team | Magicomms';
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [foundersData, members] = await Promise.all([
          getFounders(),
          getTeamMembers()
        ]);
        setFounders(foundersData);
        setTeamMembers(members);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="team-page">
      {/* Founders Section */}
      <section className="founders-section">
        <div className="team-container">
          <h1>{getText('founders_title', t('team.title'))}</h1>
          
          {loading ? (
            <div className="team-loading">Loading...</div>
          ) : (
            <div className="founders-grid">
              {founders.map((founder) => {
                const name = i18n.language === 'it' ? founder.nameIt : founder.nameEn;
                const description = i18n.language === 'it' ? founder.descriptionIt : founder.descriptionEn;
                
                return (
                  <div key={founder._id} className="founder-card">
                    <div className="founder-image">
                      {founder.picture?.asset?.url ? (
                        <img 
                          src={getOptimizedImageUrl(founder.picture, { width: 600, quality: 90 })} 
                          alt={name}
                        />
                      ) : (
                        <div className="founder-image-placeholder" />
                      )}
                    </div>
                    <div className="founder-info">
                      <h3>{name}</h3>
                      <p style={{ whiteSpace: 'pre-line' }}>{description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Team Section */}
      <section className="team-members-section">
        <div className="team-container">
          <h2>{getText('team_title', t('team.teamTitle'))}</h2>
          
          {loading ? (
            <div className="team-loading">Loading team...</div>
          ) : teamMembers.length === 0 ? (
            <p className="team-empty">Team members coming soon.</p>
          ) : (
            <div className="team-grid">
              {teamMembers.map((member) => (
                <div key={member._id} className="team-card">
                  <div className="team-card-image">
                    {member.picture?.asset?.url ? (
                      <img 
                        src={getOptimizedImageUrl(member.picture, { width: 500, quality: 85 })} 
                        alt={member.name}
                        loading="lazy"
                      />
                    ) : (
                      <div className="team-card-placeholder" />
                    )}
                  </div>
                  <div className="team-card-info">
                    <h4>{member.name}</h4>
                    <p className="team-card-job-title">{member.jobTitle}</p>
                    {member.description && (
                      <p className="team-card-description">{member.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default TeamPage;
